import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import React from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import zoomPlugin from 'chartjs-plugin-zoom'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid'
import { ChartBarIcon, FireIcon, HeartIcon, ScaleIcon } from '@heroicons/react/24/outline'
import { supabase } from './supabaseClient'
import { BoltIcon } from '@heroicons/react/24/outline'
import WeightGame from './components/WeightGame'
import { useNavigate } from 'react-router-dom'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
)

interface WeightEntry {
  date: string
  weight: number
  active_energy?: number
  body_fat_percentage?: number
  body_mass_index?: number
  cycling_distance?: number
}

type TimeRange = '1w' | '2w' | '1m' | '1y' | 'all'

// Add sound files array
const CLICK_SOUNDS = [
  '/sounds/click1.mp3',
  '/sounds/click2.mp3',
  '/sounds/click3.mp3',
  '/sounds/click4.mp3',
  '/sounds/click5.mp3',
  '/sounds/click6.mp3',
  '/sounds/click7.mp3',
  '/sounds/click8.mp3',
  '/sounds/click9.mp3'
]

type ThemeType = 'modern' | 'default' | 'ocean' | 'sunset' | 'forest'

type ThemeConfig = {
  primary: string
  secondary: string
  accent: string
  button: string
  buttonSecondary: string
  card: string
  text: string
  textSecondary: string
  glow: string
  toggle: string
  chart: {
    primary: string
    background: string
    grid: string
  }
}

function shuffleArray<T>(array: T[]): T[] {
  const newArray = [...array]
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]]
  }
  return newArray
}

interface MetricCardProps {
  title: string
  value: number | null
  unit: string
  icon: React.ComponentType<any> | (() => React.ReactNode)
  change?: number | null
  invertColors?: boolean
  onPlaySound: () => void
  onClick?: () => void
  theme: ThemeConfig
  customContent?: React.ReactNode
}

function MetricCard({ 
  title, 
  value, 
  unit, 
  icon: Icon,
  change,
  invertColors = false,
  onPlaySound,
  onClick,
  theme,
  customContent
}: MetricCardProps) {
  const isPositiveChange = change !== null && change !== undefined ? 
    (invertColors ? change < 0 : change >= 0) : 
    false

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onPlaySound()
    if (onClick) onClick()
  }
  
  return (
    <motion.div 
      className={`p-4 ${theme.card} cursor-pointer`}
      onClick={handleClick}
      whileHover={onClick ? { scale: 1.02 } : undefined}
      whileTap={onClick ? { scale: 0.98 } : undefined}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#000080] font-['MS Sans Serif']">
          {typeof Icon === 'function' && React.isValidElement(<Icon />) ? (
            <Icon className="h-5 w-5" />
          ) : (
            typeof Icon === 'function' ? <Icon /> : null
          )}
          <span className="text-sm">{title}</span>
        </div>
        {change !== undefined && change !== null && (
          <div className={`flex items-center gap-1 text-sm ${isPositiveChange ? 'text-[#008000]' : 'text-[#ff0000]'}`}>
            {change >= 0 ? <ArrowUpIcon className="h-4 w-4" /> : <ArrowDownIcon className="h-4 w-4" />}
            <span>{Math.abs(change).toFixed(1)}</span>
          </div>
        )}
      </div>
      {customContent ? (
        customContent
      ) : (
        <div className="mt-2 text-2xl font-bold text-black font-['MS Sans Serif']">
          {value !== null ? `${value.toFixed(1)}${unit}` : '-'}
        </div>
      )}
    </motion.div>
  )
}

// Common styles
const COMMON_STYLES = {
  button: {
    base: 'px-3 py-1.5 text-sm font-["MS Sans Serif"]',
    active: 'bg-[#000080] text-white border-t-[#404040] border-l-[#404040] border-b-[#c0c0c0] border-r-[#c0c0c0] border-2',
    inactive: 'bg-[#c0c0c0] text-black border-t-[#ffffff] border-l-[#ffffff] border-b-[#808080] border-r-[#808080] border-2'
  },
  card: 'bg-[#c0c0c0] border-t-[#ffffff] border-l-[#ffffff] border-b-[#808080] border-r-[#808080] border-2',
  titleBar: 'bg-[#000080] text-white px-2 py-1',
  text: {
    primary: 'text-black font-["MS Sans Serif"]',
    secondary: 'text-[#000080] font-["MS Sans Serif"]'
  }
}

function App() {
  const [currentWeight, setCurrentWeight] = useState<number | null>(null)
  const [previousWeight, setPreviousWeight] = useState<number | null>(null)
  const [weightHistory, setWeightHistory] = useState<WeightEntry[]>([])
  const [showDetailedGraph, setShowDetailedGraph] = useState(false)
  const [timeRange, setTimeRange] = useState<TimeRange>('2w')
  const [displayCount, setDisplayCount] = useState(1000)
  const [isLoaded, setIsLoaded] = useState(false)
  const [currentMetrics, setCurrentMetrics] = useState<Partial<WeightEntry>>({})
  const [previousMetrics, setPreviousMetrics] = useState<Partial<WeightEntry>>({})
  const [activeMetric, setActiveMetric] = useState<'weight' | 'energy' | 'cycling'>('weight')
  const [averageMode, setAverageMode] = useState<'raw' | 'weekly' | 'monthly'>('raw')
  const [yesterdayMetrics, setYesterdayMetrics] = useState<Partial<WeightEntry>>({})
  const [soundQueue, setSoundQueue] = useState<string[]>([])
  const [clickEffects, setClickEffects] = useState<Array<{ id: number; x: number; y: number }>>([])
  const [showGame, setShowGame] = useState(false)
  const [showOptions, setShowOptions] = useState(false)
  const [options, setOptions] = useState({
    enableSounds: false,
    enableAnimations: true,
    enableClickEffects: true,
    darkMode: true,
    theme: 'modern' as ThemeType,
    showPoopCard: false
  })

  // Add state for last poop
  const [lastPoop, setLastPoop] = useState<string | null>(null)

  const headingText = "Gutzeit Weight Tracker"
  const words = headingText.split(" ")
  const letterAnimationVariants = {
    initial: { 
      opacity: 0,
      y: 20,
      rotate: -10,
      scale: 0.5
    },
    animate: (i: number) => ({
      opacity: 1,
      y: 0,
      rotate: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        repeat: 0,
        ease: [0.2, 0.65, 0.3, 0.9],
        delay: i * 0.1
      }
    }),
    hover: {
      scale: 1.1,
      y: -5,
      transition: {
        duration: 0.2,
        ease: "easeOut"
      }
    }
  }

  const waveAnimation = {
    initial: { 
      scaleX: 0.8,
      opacity: 0 
    },
    animate: {
      scaleX: 1,
      opacity: [0, 1, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  }

  const glowAnimation = {
    initial: { 
      opacity: 0,
      scale: 0.5
    },
    animate: {
      opacity: [0, 0.5, 0],
      scale: [0.5, 1.2, 0.5],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  }

  // Theme configuration
  const themeConfig = {
    modern: {
      primary: 'bg-[#c0c0c0]',
      secondary: 'bg-[#808080]',
      accent: 'bg-[#000080]',
      button: COMMON_STYLES.button.inactive,
      buttonSecondary: COMMON_STYLES.button.inactive,
      card: COMMON_STYLES.card,
      text: COMMON_STYLES.text.primary,
      textSecondary: COMMON_STYLES.text.secondary,
      glow: '',
      toggle: 'bg-[#008080]',
      chart: {
        primary: 'rgb(0, 0, 128)',
        background: 'rgba(0, 0, 128, 0.1)',
        grid: 'rgba(128, 128, 128, 0.2)'
      }
    },
    default: {
      primary: 'bg-[#c0c0c0]',
      secondary: 'bg-[#808080]',
      accent: 'bg-[#000080]',
      button: COMMON_STYLES.button.inactive,
      buttonSecondary: COMMON_STYLES.button.inactive,
      card: COMMON_STYLES.card,
      text: COMMON_STYLES.text.primary,
      textSecondary: COMMON_STYLES.text.secondary,
      glow: '',
      toggle: 'bg-[#008080]',
      chart: {
        primary: 'rgb(0, 0, 128)',
        background: 'rgba(0, 0, 128, 0.1)',
        grid: 'rgba(128, 128, 128, 0.2)'
      }
    },
    ocean: {
      primary: 'bg-[#c0c0c0]',
      secondary: 'bg-[#808080]',
      accent: 'bg-[#000080]',
      button: COMMON_STYLES.button.inactive,
      buttonSecondary: COMMON_STYLES.button.inactive,
      card: COMMON_STYLES.card,
      text: COMMON_STYLES.text.primary,
      textSecondary: COMMON_STYLES.text.secondary,
      glow: '',
      toggle: 'bg-[#008080]',
      chart: {
        primary: 'rgb(0, 0, 128)',
        background: 'rgba(0, 0, 128, 0.1)',
        grid: 'rgba(128, 128, 128, 0.2)'
      }
    },
    sunset: {
      primary: 'bg-[#c0c0c0]',
      secondary: 'bg-[#808080]',
      accent: 'bg-[#000080]',
      button: COMMON_STYLES.button.inactive,
      buttonSecondary: COMMON_STYLES.button.inactive,
      card: COMMON_STYLES.card,
      text: COMMON_STYLES.text.primary,
      textSecondary: COMMON_STYLES.text.secondary,
      glow: '',
      toggle: 'bg-[#008080]',
      chart: {
        primary: 'rgb(0, 0, 128)',
        background: 'rgba(0, 0, 128, 0.1)',
        grid: 'rgba(128, 128, 128, 0.2)'
      }
    },
    forest: {
      primary: 'bg-[#c0c0c0]',
      secondary: 'bg-[#808080]',
      accent: 'bg-[#000080]',
      button: COMMON_STYLES.button.inactive,
      buttonSecondary: COMMON_STYLES.button.inactive,
      card: COMMON_STYLES.card,
      text: COMMON_STYLES.text.primary,
      textSecondary: COMMON_STYLES.text.secondary,
      glow: '',
      toggle: 'bg-[#008080]',
      chart: {
        primary: 'rgb(0, 0, 128)',
        background: 'rgba(0, 0, 128, 0.1)',
        grid: 'rgba(128, 128, 128, 0.2)'
      }
    }
  }

  const activeTheme = themeConfig[options.theme]

  const navigate = useNavigate()

  useEffect(() => {
    fetchWeightData()
  }, [])

  const fetchWeightData = async () => {
    try {
      const { data, error } = await supabase
        .from('weight_entries')
        .select('*')
        .order('date', { ascending: true })

      if (error) throw error

      if (data && data.length > 0) {
        setWeightHistory(data)
        
        // Find yesterday's date in YYYY-MM-DD format
        const today = new Date()
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        const yesterdayStr = yesterday.toISOString().split('T')[0]
        
        // Find the entry for yesterday and day before yesterday
        const yesterdayEntry = data.find(entry => entry.date.startsWith(yesterdayStr))
        
        const twoDaysAgo = new Date(yesterday)
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 1)
        const twoDaysAgoStr = twoDaysAgo.toISOString().split('T')[0]
        const dayBeforeYesterdayEntry = data.find(entry => entry.date.startsWith(twoDaysAgoStr))
        
        // Set yesterday's metrics for energy and cycling
        setYesterdayMetrics(yesterdayEntry || {})
        
        // Set current metrics from the latest entry
        const latestEntry = data[data.length - 1]
        const previousEntry = data[data.length - 2] || {}
        setCurrentMetrics(latestEntry)
        setCurrentWeight(latestEntry.weight)
        setPreviousWeight(data[data.length - 2]?.weight || null)
        
        // Set previous metrics for energy/cycling comparison
        setPreviousMetrics({
          ...previousEntry,
          active_energy: dayBeforeYesterdayEntry?.active_energy,
          cycling_distance: dayBeforeYesterdayEntry?.cycling_distance
        })
        
        setTimeout(() => setIsLoaded(true), 100)
      }
    } catch (error) {
      console.error('Error fetching weight data:', error)
    }
  }

  const getWeightDifference = () => {
    if (currentWeight === null || previousWeight === null) return null
    return (currentWeight - previousWeight).toFixed(1)
  }

  const getWeightAge = () => {
    if (!weightHistory.length) return null
    const lastEntry = weightHistory[weightHistory.length - 1]
    const lastDate = new Date(lastEntry.date)
    const now = new Date()
    // Add timezone offset and subtract 6 hours
    const offsetHours = -6
    const diffInMs = now.getTime() - lastDate.getTime() + 
      (now.getTimezoneOffset() * 60 * 1000) + 
      (offsetHours * 60 * 60 * 1000)
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
      return `${diffInMinutes}m ago`
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`
    } else {
      const diffInDays = Math.floor(diffInHours / 24)
      return `${diffInDays}d ago`
    }
  }

  const filteredWeightHistory = useMemo(() => {
    const now = new Date()
    const data = [...weightHistory]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-displayCount) // Limit the number of points

    switch (timeRange) {
      case '1w':
        return data.filter(entry => {
          const date = new Date(entry.date)
          return (now.getTime() - date.getTime()) <= 7 * 24 * 60 * 60 * 1000
        })
      case '2w':
        return data.filter(entry => {
          const date = new Date(entry.date)
          return (now.getTime() - date.getTime()) <= 14 * 24 * 60 * 60 * 1000
        })
      case '1m':
        return data.filter(entry => {
          const date = new Date(entry.date)
          return (now.getTime() - date.getTime()) <= 30 * 24 * 60 * 60 * 1000
        })
      case '1y':
        return data.filter(entry => {
          const date = new Date(entry.date)
          return (now.getTime() - date.getTime()) <= 365 * 24 * 60 * 60 * 1000
        })
      default:
        return data
    }
  }, [weightHistory, timeRange, displayCount])

  const getWeeklyAverages = (data: WeightEntry[]) => {
    const weeklyData: { [key: string]: number[] } = {}
    
    data.forEach(entry => {
      const date = new Date(entry.date)
      const day = date.getDay()
      const diff = date.getDate() - day + (day === 0 ? -6 : 1)
      const monday = new Date(date.setDate(diff))
      const weekKey = monday.toISOString().split('T')[0]
      const value = activeMetric === 'weight' 
        ? entry.weight 
        : activeMetric === 'energy'
        ? entry.active_energy || 0
        : entry.cycling_distance || 0
      
      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = []
      }
      weeklyData[weekKey].push(value)
    })

    return Object.entries(weeklyData).map(([weekKey, values]) => ({
      date: weekKey,
      value: activeMetric === 'cycling'
        ? values.reduce((sum, val) => sum + val, 0) // Sum for cycling
        : values.reduce((sum, val) => sum + val, 0) / values.length // Average for others
    }))
  }

  const getMonthlyAverages = (data: WeightEntry[]) => {
    const monthlyData: { [key: string]: number[] } = {}
    
    data.forEach(entry => {
      const date = new Date(entry.date)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const value = activeMetric === 'weight' 
        ? entry.weight 
        : activeMetric === 'energy'
        ? entry.active_energy || 0
        : entry.cycling_distance || 0
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = []
      }
      monthlyData[monthKey].push(value)
    })

    return Object.entries(monthlyData).map(([monthKey, values]) => ({
      date: monthKey + '-01',
      value: activeMetric === 'cycling'
        ? values.reduce((sum, val) => sum + val, 0) // Sum for cycling
        : values.reduce((sum, val) => sum + val, 0) / values.length // Average for others
    }))
  }

  const processedData = useMemo(() => {
    switch (averageMode) {
      case 'weekly':
        return getWeeklyAverages(filteredWeightHistory)
      case 'monthly':
        return getMonthlyAverages(filteredWeightHistory)
      default:
        return filteredWeightHistory.map(entry => ({
          date: entry.date,
          value: activeMetric === 'weight' 
            ? entry.weight 
            : activeMetric === 'energy'
            ? entry.active_energy || 0
            : entry.cycling_distance || 0
        }))
    }
  }, [filteredWeightHistory, averageMode, activeMetric])

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: options.enableAnimations ? 750 : 0,
      easing: 'easeInOutQuart'
    },
    plugins: {
      legend: {
        display: false
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: showDetailedGraph
          },
          pinch: {
            enabled: showDetailedGraph
          },
          mode: 'x' as const
        },
        pan: {
          enabled: showDetailedGraph,
          mode: 'x' as const
        }
      },
      tooltip: {
        enabled: showDetailedGraph,
        backgroundColor: '#c0c0c0',
        titleColor: '#000000',
        bodyColor: '#000000',
        borderColor: '#808080',
        borderWidth: 2,
        padding: 12,
        displayColors: false,
        titleFont: {
          family: 'MS Sans Serif'
        },
        bodyFont: {
          family: 'MS Sans Serif'
        },
        callbacks: {
          label: (context: any) => {
            const unit = activeMetric === 'weight' 
              ? 'kg' 
              : activeMetric === 'energy'
              ? 'kcal'
              : 'km'
            return `${context.raw} ${unit}`
          }
        }
      }
    },
    scales: {
      x: {
        display: showDetailedGraph,
        grid: {
          display: showDetailedGraph,
          color: '#ffffff'
        },
        ticks: {
          color: '#ffffff',
          maxRotation: 45,
          minRotation: 45,
          font: {
            family: 'MS Sans Serif'
          }
        }
      },
      y: {
        display: showDetailedGraph,
        grid: {
          display: showDetailedGraph,
          color: '#ffffff'
        },
        ticks: {
          color: '#ffffff',
          font: {
            family: 'MS Sans Serif'
          },
          callback: (value: number | string) => {
            const unit = activeMetric === 'weight' 
              ? 'kg' 
              : activeMetric === 'energy'
              ? 'kcal'
              : 'km'
            const decimals = activeMetric === 'weight' ? 1 : 0
            return `${Number(value).toFixed(decimals)} ${unit}`
          }
        },
        min: Math.min(...processedData.map(entry => entry.value)) - 
          (activeMetric === 'weight' ? 0.5 : activeMetric === 'energy' ? 50 : 0.5),
        max: Math.max(...processedData.map(entry => entry.value)) + 
          (activeMetric === 'weight' ? 0.5 : activeMetric === 'energy' ? 50 : 0.5)
      }
    }
  } as const

  const chartData = {
    labels: processedData.map(entry => {
      const date = new Date(entry.date)
      if (averageMode === 'monthly') {
        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
      } else if (averageMode === 'weekly') {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      } else {
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          ...(timeRange === '1y' && { year: '2-digit' })
        })
      }
    }),
    datasets: [
      {
        label: activeMetric === 'weight' 
          ? 'Weight' 
          : activeMetric === 'energy'
          ? 'Active Energy'
          : averageMode === 'monthly'
          ? 'Monthly Cycling Distance'
          : averageMode === 'weekly'
          ? 'Weekly Cycling Distance'
          : 'Cycling Distance',
        data: processedData.map(entry => entry.value),
        borderColor: '#ff0000',
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx
          const gradient = ctx.createLinearGradient(0, 0, 0, 300)
          gradient.addColorStop(0, 'rgba(255, 0, 0, 0.2)')
          gradient.addColorStop(1, 'rgba(255, 0, 0, 0)')
          return gradient
        },
        tension: !showDetailedGraph ? 0.8 : 0.4,
        fill: true,
        pointRadius: showDetailedGraph ? 4 : 0,
        pointBackgroundColor: '#ff0000',
        pointBorderColor: '#ff0000',
        pointBorderWidth: 2,
        borderWidth: !showDetailedGraph ? 1.5 : 2,
        cubicInterpolationMode: 'monotone' as const
      }
    ]
  }

  const weightDiff = getWeightDifference()

  const getMetricDifference = (current: number | null | undefined, previous: number | null | undefined) => {
    if (current === null || current === undefined || previous === null || previous === undefined) return null
    return current - previous
  }

  // Initialize sound queue
  useEffect(() => {
    setSoundQueue(shuffleArray(CLICK_SOUNDS))
  }, [])

  const playNextSound = useCallback(() => {
    if (soundQueue.length === 0) {
      // If queue is empty, reshuffle all sounds
      setSoundQueue(shuffleArray(CLICK_SOUNDS))
      return
    }

    // Play next sound from queue
    const nextSound = soundQueue[0]
    const audio = new Audio(nextSound)
    audio.volume = 0.5
    audio.play().catch(e => console.log('Error playing sound:', e))

    // Remove played sound from queue
    setSoundQueue(prev => prev.slice(1))
  }, [soundQueue])

  const handleGlobalClick = useCallback((e: React.MouseEvent) => {
    if (!options.enableClickEffects) return
    
    // Play sound if enabled
    if (options.enableSounds) {
      playNextSound()
    }

    // Add click effect
    const newEffect = {
      id: Date.now(),
      x: e.clientX,
      y: e.clientY
    }
    setClickEffects(prev => [...prev, newEffect])

    // Remove effect after animation
    setTimeout(() => {
      setClickEffects(prev => prev.filter(effect => effect.id !== newEffect.id))
    }, 1000)
  }, [playNextSound, options.enableClickEffects, options.enableSounds])

  // Add function to calculate time ago
  const getTimeAgo = (timestamp: string) => {
    const now = new Date()
    const date = new Date(timestamp)
    const diffInMs = now.getTime() - date.getTime()
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`
    } else if (diffInMinutes < 1440) { // less than 24 hours
      const hours = Math.floor(diffInMinutes / 60)
      return `${hours}h ago`
    } else {
      const days = Math.floor(diffInMinutes / 1440)
      return `${days}d ago`
    }
  }

  // Add function to fetch last poop
  const fetchLastPoop = async () => {
    if (!options.showPoopCard) return
    
    try {
      const { data, error } = await supabase
        .from('poop_records')
        .select('timestamp')
        .order('timestamp', { ascending: false })
        .limit(1)

      if (error) throw error
      setLastPoop(data?.[0]?.timestamp || null)
    } catch (error) {
      console.error('Error fetching last poop:', error)
    }
  }

  // Add useEffect to fetch last poop when showPoopCard changes
  useEffect(() => {
    fetchLastPoop()
  }, [options.showPoopCard])

  return (
    <div className="relative min-h-screen bg-[#008080] font-['MS Sans Serif']">
      {/* Click Effects */}
      {clickEffects.map(effect => (
        <motion.div
          key={effect.id}
          className="fixed pointer-events-none z-50 select-none"
          initial={{ 
            opacity: 1, 
            scale: 0.5,
            x: effect.x - 50, // Center text around cursor
            y: effect.y - 10
          }}
          animate={{ 
            opacity: 0,
            scale: 1.5,
            y: effect.y - 100 // Float upward
          }}
          exit={{ opacity: 0 }}
          transition={{ 
            duration: 1,
            ease: "easeOut"
          }}
        >
          <span className="text-sky-300 font-bold text-lg bg-clip-text text-transparent bg-gradient-to-r from-sky-200 to-sky-400">
            Snackmeyers!
          </span>
        </motion.div>
      ))}

      {/* Background Graph */}
      {!showDetailedGraph && (
        <motion.div 
          className="absolute inset-x-0 top-0 h-[100vh] opacity-50"
          initial={{ opacity: 1, scale: 1.1 }}
          animate={{ 
            opacity: isLoaded ? 0.9 : 0,
            scale: isLoaded ? 1 : 1.1,
          }}
          transition={{ 
            duration: 2,
            ease: "easeOut"
          }}
        >
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-[#008080] via-[#008080]/80 to-transparent z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 1.2 }}
          />
          <motion.div 
            className="absolute inset-0 bg-gradient-to-b from-transparent via-[#008080]/50 to-[#008080] z-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 1.2 }}
          />
          <motion.div 
            className="h-full w-full scale-150 translate-y-[-5%]"
            initial={{ y: "10%" }}
            animate={{ y: "-5%" }}
            transition={{ 
              duration: 2.5,
              ease: "easeInOut",
              delay: 0.3
            }}
          >
            <Line data={chartData} options={{
              ...chartOptions,
              animation: {
                duration: 2000,
                easing: 'easeInOutQuart'
              }
            }} />
          </motion.div>
        </motion.div>
      )}

      {/* Content */}
      <div className="relative z-10">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          {/* Windows 95 Title Bar */}
          <div className={`${COMMON_STYLES.titleBar} flex justify-between items-center`}>
            <span className="text-white font-['MS Sans Serif'] text-sm">Gutzeit Weight Tracker</span>
            <div className="flex gap-1">
              <button className={`${COMMON_STYLES.button.base} ${COMMON_STYLES.button.inactive}`}>_</button>
              <button className={`${COMMON_STYLES.button.base} ${COMMON_STYLES.button.inactive}`}>□</button>
              <button 
                onClick={() => navigate('/')}
                className={`${COMMON_STYLES.button.base} ${COMMON_STYLES.button.inactive}`}
              >X</button>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Current Weight Card */}
            <motion.div 
              className={`${activeTheme.card} p-4 md:col-span-2 lg:col-span-1 cursor-pointer`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                duration: 0.8,
                ease: "easeOut",
                delay: 0.4
              }}
              onClick={(e) => {
                e.stopPropagation()
                setActiveMetric('weight')
                setShowDetailedGraph(true)
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="relative">
                <h2 className={`text-sm font-medium mb-4 ${activeTheme.textSecondary}`}>Current Weight</h2>
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  className="absolute top-0 right-0 text-sm text-slate-400 flex items-center gap-1.5"
                >
                  <motion.div
                    className="w-1 h-1 rounded-full bg-gradient-to-r from-sky-400 to-sky-300"
                    animate={{ 
                      scale: [1, 1.5, 1],
                      opacity: [0.5, 1, 0.5],
                      background: [
                        "linear-gradient(to right, rgb(56, 189, 248), rgb(125, 211, 252))",
                        "linear-gradient(to right, rgb(125, 211, 252), rgb(186, 230, 253))",
                        "linear-gradient(to right, rgb(56, 189, 248), rgb(125, 211, 252))"
                      ]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      times: [0, 0.5, 1]
                    }}
                  />
                  <motion.div
                    className="flex items-center overflow-hidden"
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: "auto", opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    <motion.span
                      className="whitespace-nowrap text-black"
                      animate={{ 
                        backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                      }}
                      transition={{
                        duration: 5,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                      style={{
                        backgroundSize: "200% 100%"
                      }}
                    >
                      {getWeightAge()}
                    </motion.span>
                  </motion.div>
                </motion.div>
                <AnimatePresence mode="wait">
                  {currentWeight !== null && (
                    <motion.div
                      key="weight"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.5 }}
                      className="flex flex-col"
                    >
                      <div className="flex items-end gap-3">
                        <motion.span
                          initial={{ opacity: 0, scale: 0.5 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.5, delay: 1 }}
                          className="text-7xl font-bold text-black leading-[0.9]"
                        >
                          {currentWeight.toFixed(1)}
                        </motion.span>
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5, delay: 1.2 }}
                          className="text-3xl text-black mb-1"
                        >
                          kg
                        </motion.span>
                      </div>
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 1.4 }}
                        className={`flex items-center gap-2 mt-2 ${weightDiff && (Number(weightDiff) < 0 ? 'text-emerald-400' : 'text-rose-400')}`}
                      >
                        {weightDiff && (Number(weightDiff) < 0 ? (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ 
                              type: "spring",
                              stiffness: 200,
                              damping: 10,
                              delay: 1.4
                            }}
                          >
                            <ArrowDownIcon className="h-5 w-5" />
                          </motion.div>
                        ) : (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ 
                              type: "spring",
                              stiffness: 200,
                              damping: 10,
                              delay: 1.4
                            }}
                          >
                            <ArrowUpIcon className="h-5 w-5" />
                          </motion.div>
                        ))}
                        <motion.span
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: 1.6 }}
                          className="text-base"
                        >
                          {weightDiff && Math.abs(Number(weightDiff))} kg
                        </motion.span>
                        <motion.span 
                          className="text-slate-500 text-sm"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5, delay: 1.8 }}
                        >
                          since last entry
                        </motion.span>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>

          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              title="Body Fat"
              value={currentMetrics.body_fat_percentage || null}
              unit="%"
              icon={HeartIcon}
              change={getMetricDifference(
                currentMetrics.body_fat_percentage,
                previousMetrics.body_fat_percentage
              )}
              invertColors={true}
              onPlaySound={playNextSound}
              theme={activeTheme}
            />
            <MetricCard
              title="BMI"
              value={currentMetrics.body_mass_index || null}
              unit=""
              icon={ScaleIcon}
              change={getMetricDifference(
                currentMetrics.body_mass_index,
                previousMetrics.body_mass_index
              )}
              invertColors={true}
              onPlaySound={playNextSound}
              theme={activeTheme}
            />
            <MetricCard
              title="Energy Yesterday"
              value={yesterdayMetrics.active_energy || null}
              unit="kcal"
              icon={FireIcon}
              change={getMetricDifference(
                yesterdayMetrics.active_energy,
                previousMetrics.active_energy
              )}
              onPlaySound={playNextSound}
              theme={activeTheme}
            />
            <MetricCard
              title="Cycling Yesterday"
              value={yesterdayMetrics.cycling_distance || null}
              unit="km"
              icon={BoltIcon}
              change={getMetricDifference(
                yesterdayMetrics.cycling_distance,
                previousMetrics.cycling_distance
              )}
              onPlaySound={playNextSound}
              onClick={() => {
                setActiveMetric('cycling')
                setShowDetailedGraph(true)
              }}
              theme={activeTheme}
            />
            {/* Last Poop Card */}
            {options.showPoopCard && (
              <MetricCard
                title="Last Poop"
                value={null}
                unit=""
                icon={() => <span className="text-2xl">💩</span>}
                onPlaySound={playNextSound}
                theme={activeTheme}
                customContent={
                  lastPoop ? (
                    <div className="mt-2 flex flex-col gap-1">
                      <div className={`text-2xl font-bold ${activeTheme.text}`}>
                        {getTimeAgo(lastPoop)}
                      </div>
                      <div className={`text-sm ${activeTheme.textSecondary}`}>
                        {weightHistory.length > 0 && lastPoop && (
                          new Date(lastPoop) > new Date(weightHistory[weightHistory.length - 1].date)
                            ? `${getTimeAgo(weightHistory[weightHistory.length - 1].date)} after last weight`
                            : `${getTimeAgo(weightHistory[weightHistory.length - 1].date)} before last weight`
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 text-base text-slate-400">No records yet</div>
                  )
                }
              />
            )}
          </div>

          {/* Detailed Graph Section */}
          <AnimatePresence>
            {showDetailedGraph && (
              <motion.div 
                className="mt-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.5 }}
              >
                <div className="dashboard-card">
                  <div className="flex flex-col space-y-4 sm:space-y-6">
                    {/* Mobile Layout */}
                    <div className="flex flex-col space-y-4 sm:hidden">
                      {/* Time Range Buttons */}
                      <div className="flex flex-col space-y-2">
                        <label className={`text-sm font-medium ${activeTheme.textSecondary}`}>Time Range</label>
                        <div className="flex items-center gap-2">
                          {(['1w', '2w', '1m', '1y', 'all'] as const).map((range) => (
                            <button
                              key={range}
                              onClick={(e) => { 
                                e.stopPropagation()
                                if (options.enableSounds) playNextSound()
                                setTimeRange(range)
                              }}
                              className={`${COMMON_STYLES.button.base} ${
                                timeRange === range 
                                  ? COMMON_STYLES.button.active
                                  : COMMON_STYLES.button.inactive
                              }`}
                            >
                              {range}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Metric Selection */}
                      <div className="flex flex-col space-y-2">
                        <label className={`text-sm font-medium ${activeTheme.textSecondary}`}>Metric</label>
                        <div className="flex items-center gap-2">
                          {[
                            { id: 'weight', label: 'Weight' },
                            { id: 'energy', label: 'Energy' },
                            { id: 'cycling', label: 'Cycling' }
                          ].map((metric) => (
                            <button
                              key={metric.id}
                              onClick={(e) => { 
                                e.stopPropagation()
                                if (options.enableSounds) playNextSound()
                                setActiveMetric(metric.id as 'weight' | 'energy' | 'cycling')
                              }}
                              className={`${COMMON_STYLES.button.base} ${
                                activeMetric === metric.id 
                                  ? COMMON_STYLES.button.active
                                  : COMMON_STYLES.button.inactive
                              }`}
                            >
                              {metric.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Data View Mode */}
                      <div className="flex flex-col space-y-2">
                        <label className={`text-sm font-medium ${activeTheme.textSecondary}`}>Data View</label>
                        <div className="flex items-center gap-2">
                          {[
                            { id: 'raw', label: 'Raw' },
                            { id: 'weekly', label: 'Weekly' },
                            { id: 'monthly', label: 'Monthly' }
                          ].map((mode) => (
                            <button
                              key={mode.id}
                              onClick={(e) => { 
                                e.stopPropagation()
                                if (options.enableSounds) playNextSound()
                                setAverageMode(mode.id as 'raw' | 'weekly' | 'monthly')
                              }}
                              className={`${COMMON_STYLES.button.base} ${
                                averageMode === mode.id 
                                  ? COMMON_STYLES.button.active
                                  : COMMON_STYLES.button.inactive
                              }`}
                            >
                              {mode.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden sm:flex sm:items-center sm:gap-4">
                      {/* Time Range Buttons */}
                      <div className={`flex items-center gap-2 ${activeTheme.card} rounded-lg p-1`}>
                        {(['1w', '2w', '1m', '1y', 'all'] as const).map((range) => (
                          <button
                            key={range}
                            onClick={(e) => { 
                              e.stopPropagation()
                              if (options.enableSounds) playNextSound()
                              setTimeRange(range)
                            }}
                            className={`${COMMON_STYLES.button.base} ${
                              timeRange === range 
                                ? COMMON_STYLES.button.active
                                : COMMON_STYLES.button.inactive
                            }`}
                          >
                            {range}
                          </button>
                        ))}
                      </div>

                      <div className={`flex items-center gap-2 ${activeTheme.card} rounded-lg p-1`}>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation()
                            if (options.enableSounds) playNextSound()
                            setActiveMetric('weight')
                          }}
                          className={`${COMMON_STYLES.button.base} ${
                            activeMetric === 'weight' 
                              ? COMMON_STYLES.button.active
                              : COMMON_STYLES.button.inactive
                          }`}
                        >
                          Weight
                        </button>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation()
                            if (options.enableSounds) playNextSound()
                            setActiveMetric('energy')
                          }}
                          className={`${COMMON_STYLES.button.base} ${
                            activeMetric === 'energy' 
                              ? COMMON_STYLES.button.active
                              : COMMON_STYLES.button.inactive
                          }`}
                        >
                          Energy
                        </button>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation()
                            if (options.enableSounds) playNextSound()
                            setActiveMetric('cycling')
                          }}
                          className={`${COMMON_STYLES.button.base} ${
                            activeMetric === 'cycling' 
                              ? COMMON_STYLES.button.active
                              : COMMON_STYLES.button.inactive
                          }`}
                        >
                          Cycling
                        </button>
                      </div>

                      <div className={`flex items-center gap-2 ${activeTheme.card} rounded-lg p-1`}>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation()
                            if (options.enableSounds) playNextSound()
                            setAverageMode('raw')
                          }}
                          className={`${COMMON_STYLES.button.base} ${
                            averageMode === 'raw' 
                              ? COMMON_STYLES.button.active
                              : COMMON_STYLES.button.inactive
                          }`}
                        >
                          Raw
                        </button>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation()
                            if (options.enableSounds) playNextSound()
                            setAverageMode('weekly')
                          }}
                          className={`${COMMON_STYLES.button.base} ${
                            averageMode === 'weekly' 
                              ? COMMON_STYLES.button.active
                              : COMMON_STYLES.button.inactive
                          }`}
                        >
                          Weekly
                        </button>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation()
                            if (options.enableSounds) playNextSound()
                            setAverageMode('monthly')
                          }}
                          className={`${COMMON_STYLES.button.base} ${
                            averageMode === 'monthly' 
                              ? COMMON_STYLES.button.active
                              : COMMON_STYLES.button.inactive
                          }`}
                        >
                          Monthly
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="graph-container h-[400px] mt-6">
                    <Line data={chartData} options={chartOptions} />
                  </div>
                  <div className="mt-4 flex justify-end">
                    <select
                      value={displayCount}
                      onChange={(e) => { 
                        e.stopPropagation()
                        if (options.enableSounds) playNextSound()
                        setDisplayCount(Number(e.target.value))
                      }}
                      className="bg-white text-black border-t-[#808080] border-l-[#808080] border-b-[#ffffff] border-r-[#ffffff] border-2 px-2 py-1 text-sm font-['MS Sans Serif']"
                    >
                      <option value={25}>Last 25 points</option>
                      <option value={50}>Last 50 points</option>
                      <option value={100}>Last 100 points</option>
                      <option value={1000}>All points</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {showGame && (
          <WeightGame 
            weightHistory={weightHistory}
            onClose={() => setShowGame(false)}
          />
        )}
      </AnimatePresence>

      {/* Options Button - Windows 95 Style */}
      <motion.button
        className="fixed bottom-6 right-6 p-3 bg-[#c0c0c0] border-t-[#ffffff] border-l-[#ffffff] border-b-[#808080] border-r-[#808080] border-2"
        onClick={(e) => {
          e.stopPropagation()
          setShowOptions(!showOptions)
        }}
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.3 }}
      >
        <ChartBarIcon className="h-6 w-6 text-black" />
      </motion.button>

      {/* Options Panel - Windows 95 Style */}
      <AnimatePresence>
        {showOptions && (
          <motion.div
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 300 }}
            transition={{ type: "spring", damping: 20 }}
            className="fixed top-0 right-0 h-full w-80 bg-[#c0c0c0] border-t-[#ffffff] border-l-[#ffffff] border-b-[#808080] border-r-[#808080] border-2 p-6 shadow-xl z-40 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-8">
              <div className={`${COMMON_STYLES.titleBar} flex justify-between`}>
                <span className={COMMON_STYLES.text.primary}>Options</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (options.enableSounds) playNextSound()
                    setShowOptions(false)
                  }}
                  className={`${COMMON_STYLES.button.base} ${COMMON_STYLES.button.inactive}`}
                >
                  X
                </button>
              </div>

              <div className="space-y-6 p-4">
                {/* Theme Selector */}
                <div className="space-y-4">
                  <div className={`${COMMON_STYLES.titleBar} flex items-center gap-2`}>
                    <span className={COMMON_STYLES.text.primary}>Theme</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { id: 'modern', label: 'Modern' },
                      { id: 'default', label: 'Default' },
                      { id: 'ocean', label: 'Ocean' },
                      { id: 'sunset', label: 'Sunset' },
                      { id: 'forest', label: 'Forest' }
                    ].map(theme => (
                      <button
                        key={theme.id}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (options.enableSounds) playNextSound()
                          setOptions(prev => ({
                            ...prev,
                            theme: theme.id as ThemeType
                          }))
                        }}
                        className={`${COMMON_STYLES.button.base} ${
                          options.theme === theme.id 
                            ? COMMON_STYLES.button.active
                            : COMMON_STYLES.button.inactive
                        }`}
                      >
                        {theme.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-4">
                  <div className={`${COMMON_STYLES.titleBar} flex items-center gap-2`}>
                    <span className={COMMON_STYLES.text.primary}>Features</span>
                  </div>
                  <div className="space-y-3">
                    {[
                      { id: 'enableSounds', label: 'Sound Effects', icon: '🔊' },
                      { id: 'enableAnimations', label: 'Animations', icon: '✨' },
                      { id: 'enableClickEffects', label: 'Click Effects', icon: '👆' },
                      { id: 'darkMode', label: 'Dark Mode', icon: '🌙' },
                      { id: 'showPoopCard', label: 'Show Poop Card', icon: '💩' }
                    ].map(feature => (
                      <div key={feature.id} 
                        className={`${COMMON_STYLES.card} flex items-center justify-between p-2`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{feature.icon}</span>
                          <label className={COMMON_STYLES.text.primary}>{feature.label}</label>
                        </div>
                        <div 
                          className={`w-4 h-4 border-2 ${
                            options[feature.id as keyof typeof options]
                              ? 'bg-[#000080] border-t-[#404040] border-l-[#404040] border-b-[#c0c0c0] border-r-[#c0c0c0]'
                              : 'bg-white border-t-[#808080] border-l-[#808080] border-b-[#ffffff] border-r-[#ffffff]'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (options.enableSounds) playNextSound()
                            setOptions(prev => ({
                              ...prev,
                              [feature.id]: !prev[feature.id as keyof typeof prev]
                            }))
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (options.enableSounds) playNextSound()
                      setShowOptions(false)
                      setShowGame(true)
                    }}
                    className={`${COMMON_STYLES.button.base} ${COMMON_STYLES.button.inactive}`}
                  >
                    Play Weight Flappy Game
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      if (options.enableSounds) playNextSound()
                      setOptions({
                        enableSounds: false,
                        enableAnimations: true,
                        enableClickEffects: true,
                        darkMode: true,
                        theme: 'modern' as ThemeType,
                        showPoopCard: false
                      })
                    }}
                    className={`${COMMON_STYLES.button.base} ${COMMON_STYLES.button.inactive}`}
                  >
                    Reset to Defaults
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default App